import React from 'react';
import { ResumeData } from '../../../types/resume';

export function MinimalTemplate({ data }: { data: ResumeData }) {
  const { personalInfo, experience, education, skills, certifications } = data;

  if (!personalInfo) return null;

  return (
    <div className="p-[20mm] min-h-[297mm] font-serif text-gray-900 leading-relaxed">
      {/* Header */}
      <header className="text-center mb-6 pb-4 border-b border-gray-300">
        <h1 className="text-3xl font-bold mb-2">{personalInfo.fullName}</h1>
        <p className="text-xl text-gray-700 mb-2">{personalInfo.title}</p>
        <div className="text-sm text-gray-600 flex flex-wrap justify-center gap-2">
          <span>{personalInfo.email}</span>
          <span className="hidden sm:inline">•</span>
          <span>{personalInfo.phone}</span>
          <span className="hidden sm:inline">•</span>
          <span>{personalInfo.location}</span>
        </div>
      </header>

      {/* Summary */}
      {personalInfo.summary && (
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-3 text-gray-800">Professional Summary</h2>
          <p className="text-base leading-relaxed">{personalInfo.summary}</p>
        </section>
      )}

      {/* Experience */}
      {experience && experience.length > 0 && (
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-4 text-gray-800">Professional Experience</h2>
          <div className="space-y-4">
            {experience.map((exp) => (
              <div key={exp.id}>
                <div className="flex justify-between items-baseline mb-1">
                  <h3 className="text-lg font-semibold">{exp.position}</h3>
                  <span className="text-sm text-gray-600">
                    {exp.startDate} – {exp.current ? 'Present' : exp.endDate}
                  </span>
                </div>
                <p className="text-base text-gray-700 mb-2">{exp.company} • {exp.location}</p>
                <p className="text-base mb-3 leading-relaxed">{exp.description}</p>
                {exp.achievements.length > 0 && (
                  <ul className="list-disc list-inside text-base space-y-2 ml-4">
                    {exp.achievements.map((achievement, index) => (
                      <li key={index}>{achievement}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Education */}
      {education && education.length > 0 && (
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-4 text-gray-800">Education</h2>
          <div className="space-y-3">
            {education.map((edu) => (
              <div key={edu.id}>
                <div className="flex justify-between items-baseline">
                  <h3 className="text-lg font-semibold">{edu.school}</h3>
                  <span className="text-sm text-gray-600">{edu.graduationDate}</span>
                </div>
                <p className="text-base text-gray-700">{edu.degree} in {edu.field}</p>
                {edu.gpa && <p className="text-sm text-gray-600">GPA: {edu.gpa}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Skills */}
      {skills && skills.length > 0 && (
        <section className="mb-6">
          <h2 className="text-xl font-bold mb-4 text-gray-800">Skills</h2>
          <div className="text-base">
            {Object.entries(
              skills.reduce((acc, skill) => {
                if (!acc[skill.category]) acc[skill.category] = [];
                acc[skill.category].push(skill.name);
                return acc;
              }, {} as Record<string, string[]>)
            ).map(([category, categorySkills]) => (
              <div key={category} className="mb-2">
                <span className="font-medium">{category}:</span>{' '}
                {categorySkills.join(', ')}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Certifications */}
      {certifications && certifications.length > 0 && (
        <section>
          <h2 className="text-lg font-bold mb-3 text-gray-800">Certifications</h2>
          <div className="space-y-2">
            {certifications.map((cert) => (
              <div key={cert.id} className="text-sm">
                <span className="font-semibold">{cert.name}</span> - {cert.issuer}
                <span className="text-gray-600"> ({cert.issueDate})</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}